/**
 * Where a student's lesson day and next lesson come from.
 *
 * THE BUG THIS EXISTS TO FIX (2026-07-27). The Students roster and the Student
 * Profile Overview both derived "Lesson Day" from `students.nextLesson` — an
 * MMS-imported shadow field. Every student who came in through the CSV import
 * carries it, so the column looked correct; the first student placed through
 * the portal's OWN /register flow (Cillian Murphy) has no such field, and both
 * surfaces rendered a dash beside a lesson that plainly existed in the grid.
 *
 * That is backwards for the fall: `timeSlots` is the canonical placement and
 * `nextLesson` is the legacy adapter. So the order here is timeSlots FIRST,
 * legacy only as a fallback — which means the column self-heals as students
 * move off MMS, and no data migration is required for it to start telling the
 * truth. (Retiring `nextLesson` outright is a separate call; this reads it
 * rather than deleting it.)
 *
 * Lesson day and next lesson are resolved from DIFFERENT sources on purpose:
 * the day is the RECURRING slot's weekday (a weekly student's standing day),
 * while the next lesson is the soonest occurrence across ALL their assigned
 * slots — so a Tuesday makeup shows up as the next lesson without rewriting a
 * Sunday student's lesson day to Tuesday.
 */

import { expandUpcomingLessons, ymd } from './occurrenceExpansion';
import { seasonLookaheadDays } from '../data/seasonData';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Legacy `students.nextLesson` parse. Older EditStudent saves stored a UTC
// midnight, which renders a day early in Central; MMS values carry a real
// lesson time and render correctly. Coerce a date-only / UTC-midnight value to
// its intended local calendar day (#14). Moved here verbatim from Students.js
// so the roster and the profile cannot drift on it.
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.000)?Z)?$/;

export const legacyNextLessonDate = (val) => {
	if (!val) return null;
	const m = typeof val === 'string' && val.match(DATE_ONLY_RE);
	const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(val);
	return isNaN(d) ? null : d;
};

// studentId → that student's assigned slots.
export const indexSlotsByStudent = (slots) => {
	const map = new Map();
	(slots || []).forEach((s) => {
		if (!s || !s.studentId) return;
		if (!map.has(s.studentId)) map.set(s.studentId, []);
		map.get(s.studentId).push(s);
	});
	return map;
};

// The student's standing weekly day. Canonical source is the recurring slot;
// falls back to deriving it from the legacy field the way the roster always did.
export const lessonDayFor = (slots, student) => {
	const recurring = (slots || []).find((s) => s.isRecurring && typeof s.dayOfWeek === 'number');
	if (recurring) return DAY_NAMES[recurring.dayOfWeek] || null;
	const legacy = legacyNextLessonDate(student && student.nextLesson);
	return legacy ? DAY_NAMES[legacy.getDay()] : null;
};

// Soonest real occurrence across every assigned slot, as 'YYYY-MM-DD'. Uses the
// shared expander, so closures, flex days, startDate/endDate and per-date
// cancellations are all honored — a roster date that ignored them would point
// staff at a lesson that is not happening.
export const nextLessonYmdFor = (slots, from = new Date()) => {
	if (!slots || slots.length === 0) return null;
	const start = new Date(from);
	start.setHours(0, 0, 0, 0);
	const next = expandUpcomingLessons(slots, start, seasonLookaheadDays(start))[0];
	return next ? ymd(next._date) : null;
};

// Row decoration for a roster/profile: canonical when a placement exists,
// legacy untouched when it does not. Returns the fields the surfaces render on.
export const withScheduleFields = (student, slots, from = new Date()) => {
	const nextYmd = nextLessonYmdFor(slots, from);
	return {
		...student,
		// Overwritten only when the grid actually knows — otherwise the legacy
		// MMS value stays exactly as it was, so nothing regresses for the 75
		// imported students who have no timeSlot yet.
		nextLesson: nextYmd || student.nextLesson,
		lessonDay: lessonDayFor(slots, student),
	};
};
